import React, { useState, useEffect, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useTable, usePagination, useSortBy, useGlobalFilter } from "react-table";
import Select from "react-select";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faEye, faEdit, faPlus, faCheck, faCheckCircle, faPencil } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";

/* -------------------- GLOBAL FILTER -------------------- */
const GlobalFilter = ({ filter, setFilter }) => (
  <input
    value={filter || ""}
    onChange={(e) => setFilter(e.target.value)}
    placeholder="Search..."
    className="form-control"
  />
);

/* -------------------- TABLE COMPONENT -------------------- */
const TableComponent = ({ columns, data, globalFilter, pageSize, setPageSize }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    previousPage,
    nextPage,
    setGlobalFilter,
    setPageSize: syncPageSize,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize, globalFilter },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { pageIndex } = state;

  /* Sync external filter */
  useEffect(() => {
    setGlobalFilter(globalFilter);
  }, [globalFilter]);

  /* Sync parent page size */
  useEffect(() => {
    syncPageSize(pageSize);
  }, [pageSize]);

  return (
    <>
      <table
        className="table table-striped table-bordered"
        {...getTableProps()}
        style={{ fontSize: "13px" }}
      >
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())}  style={{
                  cursor: "pointer",
                  width: col.width,
                  minWidth: col.minWidth,
                  maxWidth: col.maxWidth,
                }}>
                  {col.render("Header")}
                  {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);

            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    style={{
                      color:
                        row.original.status === "Draft"
                          ? "#0d6efd"
                          : row.original.status === "Approved"
                          ? "#198754"
                          : "inherit",
                    }}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* PAGINATION */}
      <div className="d-flex justify-content-between align-items-center">
        <button
          className="btn btn-secondary"
          disabled={!canPreviousPage}
          onClick={previousPage}
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
        </span>

        <button
          className="btn btn-secondary"
          disabled={!canNextPage}
          onClick={nextPage}
        >
          Next
        </button>
      </div>
    </>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */
const OpeningStockLedgerList = ({ accessedLedgers, permissions }) => {
  const navigate = useNavigate();
  const code = localStorage.getItem("code");

  const [tableData, setTableData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageSize, setPageSize] = useState(20);

  /* Status Filter Dropdown */
  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "all", label: "All" },
  ];

  /* -------------------- LOAD DATA -------------------- */
  const loadData = async () => {
    setLoadingData(true);

    try {
      if (statusFilter === "pending") {
        const { data } = await apiClient.get(
          SERVER_BASE + "/getAllDraftOpeningStockLedgers",
          { params: { code, accessedLedgers } }
        );
        setTableData(data || []);
      } else if (statusFilter === "approved") {
        const { data } = await apiClient.get(
          SERVER_BASE + "/getAllApprovedOpeningStockLedgers",
          { params: { code, accessedLedgers } }
        );
        setTableData(data || []);
      } else {
        const [p, a] = await Promise.all([
          apiClient.get(SERVER_BASE + "/getAllDraftOpeningStockLedgers", {
            params: { code, accessedLedgers },
          }),
          apiClient.get(SERVER_BASE + "/getAllApprovedOpeningStockLedgers", {
            params: { code, accessedLedgers },
          }),
        ]);

        setTableData([...(p.data || []), ...(a.data || [])]);
      }
    } catch (err) {
      console.error("Error loading opening stock ledgers:", err);
      setTableData([]);
    }

    setLoadingData(false);
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  /* -------------------- TABLE COLUMNS -------------------- */
  const columns = useMemo(
    () => [
      { Header: "S.No", Cell: ({ row }) => row.index + 1 },
      {Header: "Date",accessor: "createdAt",Cell: ({ value }) =>
          value ? moment(value).format("DD-MMM-YY") : "",}, 
      { Header: "Ref No.", accessor: "openingStockNo" },
      { Header: "Ledger",width: 200,
        minWidth: 200,
        maxWidth: 200, accessor: "ledgerName" },
      { Header: "Count", accessor: "itemCount" },
      { Header: "Items",  width: 510,
        minWidth: 510,
        maxWidth: 510, accessor: "printNames" },

      { Header: "Status", accessor: "status" },
      { Header: 'Preview', accessor: 'Preview',Cell:({row})=>{
        const r=row.original
        return (
          permissions?.read && 
          <button
          className="btn btn-sm btn-outline-success"
                    onClick={() =>
                      navigate("../../PreviewOpeningStockLedger", {
                        state: { id: r.id },
                      })
                    }
                  >
                    View
                  </button>
        )
      } },
      {
        Header: "#",
        Cell: ({ row }) => {
          const r = row.original;

          return (
            <div className="dropdown">
              <button
                className="btn btn-light btn-sm"
                data-bs-toggle="dropdown"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul className="dropdown-menu dropdown-menu-end">
                {r.status === "Draft" && permissions?.edit && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() =>
                        navigate("../OpeningStockLedger", { state: { rowData: r } })
                      }
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </button>
                  </li>
                )}

                <li>
                  <button
                    className="dropdown-item"
                    onClick={() =>
                      navigate("../../PreviewOpeningStockLedger", {
                        state: { id: r.id },
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faEye} /> Preview
                  </button>
                </li>

                <li>
                  <hr />
                </li>
                <li>
                  {r.createdBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPlus} /> {r.createdBy}
                      {r.createdAt &&
                        ` (${moment(r.createdAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {r.updatedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPencil} /> {r.updatedBy}
                      {r.updatedAt &&
                        ` (${moment(r.updatedAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {r.approvedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faCheckCircle} /> {r.approvedBy}
                      {r.approvedAt &&
                        ` (${moment(r.approvedAt).format(
                          "DD-MMM-YY hh:mm A"
                        )})`}
                    </li>
                  )}
                </li>
              </ul>
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  return (
    <div className="formcontainer mt-5">
      <h4 className="mb-3">Ledger Opening Stock</h4>

      {/* TOP BAR */}
      <div className="row align-items-center mb-3">

        {/* Add */}
        {permissions?.write && (
          <div className="col-md-2 mb-2">
            <button
              className="btn btn-outline-success w-100"
              onClick={() => navigate("../OpeningStockLedger")}
            >
              Add
            </button>
          </div>
        )}

        {/* Search */}
        <div className="col-md-5 mb-2">
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>

        {/* Status Filter */}
        <div className="col-md-3 mb-2">
          <Select
            options={statusOptions}
            value={statusOptions.find((o) => o.value === statusFilter)}
            onChange={(v) => setStatusFilter(v.value)}
          />
        </div>

        {/* Page Size */}
        <div className="col-md-2 mb-2">
          <select
            className="form-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[20, 30, 50, 100].map((s) => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </div>

      </div>

      {/* TABLE */}
      {loadingData ? (
        <p>Loading...</p>
      ) : (
        <TableComponent
          columns={columns}
          data={tableData}
          globalFilter={globalFilter}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </div>
  );
};

export default OpeningStockLedgerList;
